// Director dashboard — lists all enrolled students with a scheduled last day.
// Data comes from the Redux students slice (already loaded app-wide); no
// additional Firestore query needed. Prorate math reuses calcProration from
// unenrollHelpers.ts anchored to the stored noticeGivenOn date; rows where
// that field is absent (pre-form records) show — instead of a drifting estimate.

import { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Badge, ColorPaletteEnum, ColumnConfig, DataTable, MainContentLayout } from '@wonderschool/common-base-ui';

import { getTitlePrefix } from '../../../config/env';
import { toDateObject } from '../../../helpers/dates';
import { paths } from '../../../navigation/paths';
import { useStudents } from '../../studentsHooks';
import {
  bestDepositCandidate,
  buildDepositCandidates,
  calcProration,
  DAYS_PER_MONTH,
  extractParentName,
  fmtCents,
  fmtShortDate,
  studentFullName,
  type ProrationResult,
} from './unenrollHelpers';

type PendingRow = {
  studentId: string;
  studentName: string;
  parentName: string;
  lastDay: Date;
  // Positive = days remaining; 0 = today; negative = days overdue (cron pending).
  daysLeft: number;
  hasTuition: boolean;
  // False when noticeGivenOn is absent (records created before the field existed,
  // or submitted via a non-form path). In that case all proration columns show —
  // rather than a fee that drifts daily as calcProration anchors to today.
  hasNoticeDate: boolean;
  depositHeldCents: number;
  // null when no notice period was configured at submission time — all proration columns show —.
  proration: ProrationResult | null;
};

function toLocalDateStr(d: Date): string {
  // Firestore stores unenroll dates as midnight UTC. Use UTC accessors so
  // the string matches the calendar date the director entered regardless of
  // the browser's local timezone offset.
  return d.toISOString().slice(0, 10);
}

function daysUntil(target: Date): number {
  const now = new Date();
  // Use UTC date components for both sides — Firestore dates are midnight
  // UTC so mixing local accessors (getDate) would shift the result by one
  // day in negative-offset timezones (e.g. CST/PDT).
  const fromMs = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const toMs = Date.UTC(target.getUTCFullYear(), target.getUTCMonth(), target.getUTCDate());
  return Math.floor((toMs - fromMs) / 86400000);
}

export function UnenrollmentsPage() {
  const { t } = useTranslation();
  // listFiltered, not list: `list` is the raw Redux slice and bypasses every filter.
  // Archived students must not appear here — getStudentsToUnenroll
  // (functions/src/api/students-ts.ts) skips them, so one archived while still
  // enrollment.status === 'enrolled' is never finalized and would otherwise sit on this
  // dashboard as permanently overdue with live prorate math.
  const { listFiltered: students } = useStudents({ filters: { hideArchivedStudents: true }, searchText: '' });

  const rows = useMemo<PendingRow[]>(() => {
    const results: PendingRow[] = [];

    for (const student of students) {
      const enrollment = (student as any).enrollment;
      // Only enrolled students with a scheduled last day belong here.
      if (enrollment?.status !== 'enrolled') continue;
      if (!enrollment?.unenrolled?.date) continue;

      const lastDay = toDateObject(enrollment.unenrolled.date);
      if (!lastDay) continue;

      // Keep monthlyTuitionCents for the hasTuition zero-check and as a legacy
      // fallback. Records created by the CCMS-8118+ form also store dailyTuitionCents
      // directly; prefer that to avoid the monthly→daily round-trip approximation.
      const monthlyTuitionCents: number =
        enrollment?.unenrolled?.monthlyTuitionCents ?? enrollment?.tuitionAndFees?.tuition?.amount ?? 0;
      const dailyTuitionCents: number =
        enrollment?.unenrolled?.dailyTuitionCents ?? monthlyTuitionCents / DAYS_PER_MONTH;

      // noticeDaysApplied is written by the unenroll form. undefined means no
      // notice period was configured at submission time — proration shows —.
      const noticeDaysApplied: number | undefined = enrollment?.unenrolled?.noticeDaysApplied;

      // Resolve the deposit the same way the form does so the two surfaces
      // always agree on refundability and amount.
      const depositCandidate = bestDepositCandidate(buildDepositCandidates(student));
      const depositHeldCents = depositCandidate?.refundable ? depositCandidate.amountCents : 0;

      // noticeGivenOn anchors the prorate math to the original submission date.
      // When absent (records predating the field or non-form writes) we still
      // call calcProration but flag hasNoticeDate=false so columns render — rather
      // than a fee that drifts daily as calcProration would fall back to today.
      const hasNoticeDate = Boolean(enrollment?.unenrolled?.noticeGivenOn);
      const noticeGivenOn = toDateObject(enrollment?.unenrolled?.noticeGivenOn) ?? undefined;

      const lastDayStr = toLocalDateStr(lastDay);
      const proration =
        noticeDaysApplied !== undefined
          ? calcProration(dailyTuitionCents, noticeDaysApplied, depositHeldCents, lastDayStr, noticeGivenOn)
          : null;

      results.push({
        studentId: (student as any).id,
        studentName: studentFullName(student) || (student as any).displayName || '',
        parentName: extractParentName(student),
        lastDay,
        daysLeft: daysUntil(lastDay),
        hasTuition: monthlyTuitionCents > 0,
        hasNoticeDate,
        depositHeldCents,
        proration,
      });
    }

    // Soonest last day first; overdue rows (negative) sort to the top.
    return results.sort((a, b) => a.daysLeft - b.daysLeft);
  }, [students]);

  const columns = useMemo<ColumnConfig<PendingRow>[]>(
    () => [
      {
        fieldName: 'studentName',
        label: t('unenrollments.dashboard.col.student'),
        renderCell: (row) => (
          <div>
            <Link
              to={paths.studentDetails.replace(':studentId', row.studentId)}
              className="font-medium text-blue-700 hover:text-blue-900 hover:underline"
            >
              {row.studentName}
            </Link>
            <div className="text-xs text-slate-500">{row.parentName}</div>
          </div>
        ),
      },
      {
        fieldName: 'lastDay',
        label: t('unenrollments.dashboard.col.lastDay'),
        renderCell: (row) => <span className="whitespace-nowrap">{fmtShortDate(row.lastDay)}</span>,
      },
      {
        fieldName: 'daysLeft',
        label: t('unenrollments.dashboard.col.status'),
        renderCell: (row) => {
          if (row.daysLeft === 0)
            return <Badge label={t('unenrollments.dashboard.today')} color={ColorPaletteEnum.YELLOW} />;
          if (row.daysLeft < 0)
            return (
              <Badge
                label={t('unenrollments.dashboard.overdue', { count: -row.daysLeft })}
                color={ColorPaletteEnum.RED}
              />
            );
          return (
            <Badge
              label={t('unenrollments.dashboard.daysRemaining', { count: row.daysLeft })}
              color={ColorPaletteEnum.GRAY}
            />
          );
        },
      },
      {
        fieldName: 'proration',
        label: t('unenrollments.dashboard.col.notice'),
        renderCell: (row) => {
          if (!row.proration) return <span className="text-slate-400">—</span>;
          return row.proration.meetsNotice ? (
            <Badge label={t('unenrollments.dashboard.noticeMet')} color={ColorPaletteEnum.GREEN} />
          ) : (
            <Badge label={t('unenrollments.dashboard.noticeShort')} color={ColorPaletteEnum.YELLOW} />
          );
        },
      },
      {
        fieldName: 'shortNoticeFee',
        label: t('unenrollments.dashboard.col.shortNoticeFee'),
        className: 'text-right',
        columnClassName: 'text-right',
        renderCell: (row) => {
          if (!row.proration || !row.hasTuition) return <span className="text-slate-400">—</span>;
          return fmtCents(row.proration.prorateAmountCents);
        },
      },
      {
        fieldName: 'depositCredit',
        label: t('unenrollments.dashboard.col.deposit'),
        className: 'text-right',
        columnClassName: 'text-right',
        renderCell: (row) => {
          if (!row.proration || row.depositHeldCents === 0) return <span className="text-slate-400">—</span>;
          return fmtCents(row.proration.depositCreditCents);
        },
      },
      {
        fieldName: 'net',
        label: t('unenrollments.dashboard.col.net'),
        className: 'text-right',
        columnClassName: 'text-right',
        renderCell: (row) => {
          if (!row.proration || (!row.hasTuition && row.depositHeldCents === 0))
            return <span className="text-slate-400">—</span>;
          const net = row.proration.netOwedCents;
          if (net === 0) return <span className="text-slate-500">$0.00</span>;
          // Positive = family owes us; negative = we owe family (shown in parens).
          if (net > 0) return <span className="font-medium text-rose-700">{fmtCents(net)}</span>;
          return <span className="font-medium text-emerald-700">({fmtCents(-net)})</span>;
        },
      },
    ],
    [t]
  );

  return (
    <>
      <Helmet>
        <title>
          {getTitlePrefix()}
          {t('unenrollments.dashboard.pageTitle')}
        </title>
        <body />
      </Helmet>
      <MainContentLayout>
        <div className="mx-auto w-full max-w-6xl px-4 py-6 sm:px-6 lg:px-8">
          <div className="mb-6">
            <h1 className="text-2xl font-semibold text-slate-900">{t('unenrollments.dashboard.pageTitle')}</h1>
            <p className="mt-1 max-w-2xl text-sm text-slate-600">{t('unenrollments.dashboard.subtitle')}</p>
          </div>
          {rows.length === 0 ? (
            <div className="rounded-lg border border-slate-200 bg-white px-8 py-16 text-center">
              <p className="text-base font-medium text-slate-700">{t('unenrollments.dashboard.empty.title')}</p>
              <p className="mt-2 text-sm text-slate-500">{t('unenrollments.dashboard.empty.subtitle')}</p>
            </div>
          ) : (
            <DataTable
              data={rows as any}
              columns={columns as ColumnConfig<any>[]}
              hasPagination={false}
              hasDataCountTitle={false}
            />
          )}
        </div>
      </MainContentLayout>
    </>
  );
}

export default UnenrollmentsPage;
